// Notes SIDEBAR closure — extracted EAGER (DashboardRows-style) so the rest
// of the notes context (page bodies + fetch blocks + search utils) can defer
// out of the initial shell bundle via lazySharedLibPage.
//
// WHY this file exists (the carve-out): `DocsSidebar` is referenced OUTSIDE the
// package by three SHARED components that mount on canvas/editor/help
// routes BEFORE /notes is ever visited —
//   • shared/components/ProjectCodeSidebar.js  → extends: 'DocsSidebar'
//   • shared/components/TutorialsPanel.js       → bare-key DocsSidebar: {}
//   • shared/components/TutorialsPanelCopy.js    → bare-key DocsSidebar: {}
// Because those consumers string-extend / bare-key the WHOLE DocsSidebar tree,
// its entire dependency closure (section nav, sub-nav, filter/folder/kb lists +
// rows) must be registered eagerly too. They are moved here verbatim from
// Sidebar.js and re-exported by the component barrel, so:
//   1. sharedLibraries.js imports THIS module (only) to keep the closure eager, and
//   2. the lazy notes context (context.js → components/index.js) still sees
//      them via the barrel — the lazy merge is add-missing, so it no-ops on them.
// Nothing here imports back from a page module (that would re-pin the deferred
// page code into the initial bundle). DocsMobileSidebarBar + DocsMobileRail (the
// phone chrome) stay in Sidebar.js — they have no external consumers and load
// with the pages; only the browse column itself is shared between them.
import { DOC_FOLDERS, KB_CATEGORIES } from '../constants.js'

// Slot-aware: stays inside the home PageSlot window when the surface is
// hosted there, routes normally otherwise (functions/notesNavigate.js —
// eager-registered so these EAGER rows can el.call it).
const _documentsNavigate = (el, path) => el.call('notesNavigate', path)

// The browse column — ONE definition, TWO presentations. The desktop rail
// (DocsSidebar.Middle) and the phone sheet (DocsMobileRail.Card.Content) both
// extend this, so section/filter/category navigation can never drift between
// the two, and every row's close-on-select write hits the same rail key.
export const DocsBrowseColumn = {
  gap: 'X2',
  flow: 'y',
  padding: '0',
  // Sheet cards are only min(86vw, 340px) wide — long folder/category labels
  // must ellipsize inside the column, never widen it.
  minWidth: '0',

  SectionNav: { extends: 'DocsSectionNav', padding: 'X2' },

  // Borderless (design-direction.md §8) — the hairline divider between
  // sections is banned outright; the column's own `gap: 'X2'` already
  // separates SectionNav from DocsSubNav.

  DocsSubNav: { padding: 'X2' }
}

export const DocsSidebar = {
  extends: 'AppSidebar',
  tag: 'nav',
  'aria-label': (el) => el.call('polyglot', 'documents.sidebar.navLabel'),

  // Drop the inherited VIEWS switcher — /notes is not a canvas view. Per Nika.
  ViewSwitcher: null,
  // PHONE: the desktop rail steps aside entirely and the browse column is
  // re-presented in the shared off-canvas sheet (DocsMobileRail — see
  // ./Sidebar.js), so the reading/editing surface owns the full phone
  // viewport and /notes answers to the ONE platform gesture (left-edge
  // pull opens; scrim tap, left-flick or Escape closes). CSS-gated, not a JS
  // `hide` factory, so a rotate/resize flips it with no state write — and the
  // off-package consumers of this tree (ProjectCodeSidebar, TutorialsPanel*)
  // keep exactly the phone behaviour they had, since the old `hide` gate also
  // resolved to "hidden" for them.
  '@mobileL': { display: 'none' },

  Top: null,

  // The section rows start at the card's top: on a page that opts in (each
  // notes page, not the Learn panel), the head band keeps the hide button off
  // the first row (AppSidebar's free corner). Inert anywhere else.
  Middle: { extends: 'DocsBrowseColumn', 'data-sidebar-band': 'true' },

  // No PeopleSection on notes — the surface is about finding/editing
  // content, not browsing collaborators. Matches the tickets + meet
  // sidebars which also null this slot.
  PeopleSection: null
}

// ── Section switcher: Notes / Knowledge Base ──
// (The old three-way Documents/Notes/Knowledge Base split collapsed with the
// /documents → /notes rename: the main list IS notes — every non-KB doc type
// renders there — so a separate "Documents" section had nothing distinct left
// to show.)

const _sections = [
  { key: 'notes', label: 'documents.section.notes', icon: 'edit' },
  { key: 'knowledge-base', label: 'documents.section.knowledgeBase', icon: 'library' }
]

export const DocsSectionNav = {
  flow: 'y',
  gap: 'V',

  childExtends: 'DocsSectionRow',
  childrenAs: 'state',
  children: () => _sections
}

export const DocsSectionRow = {
  extends: 'SidebarRow',
  // Active state via a conditional class — a reactive `background`/`color`
  // factory emits an INLINE node style that clobbers ':hover'. The .isActive
  // class keeps :hover alive (verified pattern from TicketsSidebarItem).
  isActive: (el, s) => s.root.activeSection === s.key,
  // CODEX-M2 mobile targets — same 44px floor as DocsNotesFilterRow (see
  // its note; box growth, not tapSlop, because rows stack adjacently).
  '@coarse': { minHeight: 'controlH' },
  color: 'caption',
  fontWeight: '700',
  '.isActive': { background: 'selected', color: 'title' },
  ':hover': { background: 'hover', color: 'title' },
  'aria-current': (el, s) => (s.root.activeSection === s.key ? 'page' : undefined),
  // Picking a section dismisses the phone sheet — the chosen surface takes
  // the viewport immediately (same contract on every row below). Flush any
  // pending autosave FIRST (mirrors EditorHeader's BackBtn) — leaving the
  // editor via a section tap must not drop the last debounced keystrokes.
  onClick: async (e, el, s) => {
    if (s.root.editorMode === 'edit') await el.call('flushNoteAutosave')
    s.root.update({
      activeSection: s.key,
      selectedDocument: null,
      editorDoc: null,
      editorMode: null,
      editorPreview: false,
      _docsRailOpen: false
    })
    _documentsNavigate(el, s.key === 'notes' ? '/notes' : '/notes/' + s.key)
  },

  Icon: { name: (el, s) => s.icon || 'document' },
  Label: { text: (el, s) => el.call('polyglot', s.label || s.key) },

  // Section nav doesn't need three-dot menu
  MoreBtn: null
}

// ── Contextual sub-navigation (source filters + folders for notes, categories for KB) ──

// frank-allow-block FA813: the notes sub-nav (filters, folders, New folder) and the knowledge-base categories swap as a whole with the section — content, not chrome
export const DocsSubNav = {
  flow: 'y',
  gap: 'V',

  NotesFilterList: {
    extends: 'DocsNotesFilterList',
    if: (el, s) => s.root.activeSection !== 'knowledge-base'
  },

  // Borderless (design-direction.md §8) — dropped the hairline divider
  // between the filter list and the folder list; the parent's `gap: 'V'`
  // plus each list's own internal `gap` already read as two groups.

  FolderList: {
    extends: 'DocsFolderList',
    if: (el, s) => s.root.activeSection !== 'knowledge-base'
  },

  // The folder list's own last row — part of the notes sub-nav that swaps
  // with the knowledge-base categories as a whole, the same gate as its
  // siblings.
  NewFolderRow: {
    extends: 'DocsNewFolderRow',
    if: (el, s) => s.root.activeSection !== 'knowledge-base'
  },

  KbCategoryList: {
    extends: 'DocsKbCategoryList',
    if: (el, s) => s.root.activeSection === 'knowledge-base'
  }
}

// ── Scope filters (All / Shared / Private / AI) ──

const _notesFilters = [
  { key: 'all', label: 'documents.filter.all', icon: 'edit' },
  { key: 'shared', label: 'documents.filter.shared', icon: 'people' },
  { key: 'private', label: 'documents.filter.private', icon: 'lock' },
  { key: 'ai', label: 'documents.filter.ai', icon: 'star' }
]

// Copy of Main.js's `_matchesFilter` (the eager sidebar carve-out must not
// import the lazy page-component module — see the header). shared/private is
// the files-style privacy scope; legacy rows predate both fields and count
// as shared member notes.
const _matchesFilter = (row, key) => {
  if (key === 'ai') return row.source === 'ai'
  if (key === 'private') return row.privacy === 'private'
  if (key === 'shared') return row.privacy !== 'private'
  return true
}

// NOTES-MICRO-1 item 1 (tickets/sonnet.md) — "note-count/list instability":
// sidebar badge shows a DIFFERENT number than the grid it sits next to on
// the SAME `/notes` view. Root-caused by static tracing (Chrome MCP was
// unavailable this session — see the ticket): `pages/notes.js`'s `NotesMain`
// (a separately-fetched `s.root.notes` array, `type==='note'` only) is NOT
// wired into the shell's route table at all (workspace/packages/workspace/
// pages/index.js — only `pages/index.js`'s `home` → `DocsMain` serves
// `/notes`), so the two-independent-fetches race that hypothesis predicted
// doesn't reach the LIVE grid. The REAL, code-provable divergence: this
// file's `DocsNotesFilterRow.Count` / `DocsFolderRow.Count` counted against
// `s.root.documents` with ONLY the scope filter applied, while
// `Main.js`'s `_filtered` (what the grid actually RENDERS) ALSO narrows by
// `selectedFolder` and `docsQuery` — two root-state values that persist
// across navigation and aren't reset on route change. Whenever either is
// non-default (a folder scoped, or a search term still sitting in the box
// from an earlier visit), the badge showed the GRAND total while the grid
// showed a folder/query-narrowed subset — no network race required, and
// "eventually consistent" once the user cleared the folder/query (e.g. by
// tapping "All notes", which explicitly resets `selectedFolder`). Fix: apply
// the EXACT SAME three-filter pipeline (folder + scope + query) the grid
// uses, so the sidebar number can never disagree with what's on screen. A
// residual, NOT live-confirmed candidate if a mismatch is still observed
// with folder=all/query=empty: the two independent `cache: '30s'` SDK fetches
// (functions/index.js `documentsFetch`/`notesFetch`) don't key their cache by
// workspace/org, so a same-session workspace switch could serve one of them
// a stale cross-workspace snapshot for up to 30s — needs live network-timing
// capture to confirm, left as the next step if this fix doesn't fully close
// the ticket.
const _documentsMatchesQuery = (row, q) =>
  (row.title || '').toLowerCase().includes(q) ||
  (row.summary || '').toLowerCase().includes(q) ||
  (row.author || '').toLowerCase().includes(q) ||
  (row._bodyText || '').toLowerCase().includes(q) ||
  (row.tags || []).some((t) => String(t).toLowerCase().includes(q))

// Shared "current view" doc set for the notes sidebar counts — folder +
// query narrowing, WITHOUT the scope filter (each row applies its own scope
// on top, so "All notes"/"Shared"/"Private"/"AI notes" can each show their
// own count within the same folder/search view).
const _notesScopedDocs = (s) => {
  const root = s.root
  const folder = root.selectedFolder || 'all'
  const q = (root.docsQuery || '').toLowerCase().trim()
  let docs = root.documents || []
  if (folder !== 'all') docs = docs.filter((d) => d.folder === folder)
  if (q) docs = docs.filter((d) => _documentsMatchesQuery(d, q))
  return docs
}

// Same idea for the FOLDER rows — scope (notesFilter) + query narrowing,
// WITHOUT the folder filter (each row applies its own folder on top, so
// every folder can show its own count within the same scope/search view).
const _notesScopeFilteredDocs = (s) => {
  const root = s.root
  const scopeFilter = root.notesFilter || 'all'
  const q = (root.docsQuery || '').toLowerCase().trim()
  let docs = root.documents || []
  if (scopeFilter !== 'all') docs = docs.filter((d) => _matchesFilter(d, scopeFilter))
  if (q) docs = docs.filter((d) => _documentsMatchesQuery(d, q))
  return docs
}

export const DocsNotesFilterList = {
  flow: 'y',
  gap: 'V',
  childExtends: 'DocsNotesFilterRow',
  childrenAs: 'state',
  children: () => _notesFilters
}

export const DocsNotesFilterRow = {
  extends: 'SidebarRow',
  isActive: (el, s) => (s.root.notesFilter || 'all') === s.key,
  // CODEX-M2 mobile targets: SidebarRow's height is emergent from padding
  // (~42-44px, borderline vs the 44px floor). Guarantee the floor on touch
  // by growing the row's OWN box — NOT the tapSlop ::after overlay, which
  // is for isolated chips; negative-inset slop on adjacent stacked rows
  // would overlap neighbours and mis-target taps.
  '@coarse': { minHeight: 'controlH' },
  color: 'caption',
  '.isActive': { background: 'selected', color: 'title' },
  ':hover': { background: 'hover', color: 'title' },
  'aria-current': (el, s) => ((s.root.notesFilter || 'all') === s.key ? 'page' : undefined),
  // NOTES-MICRO-1 item 2 ("All notes is a no-op from inside a note") — this
  // row is also mounted on the /notes/editor ROUTE (DocsSidebar renders
  // there too, see pages/editor.js), and that route only reads
  // editorDoc/editorMode, never notesFilter/selectedFolder. Clicking it
  // while a note is open used to just write filter state the current route
  // never looks at, so nothing visibly happened. Exit the editor (flushing
  // any pending autosave first, mirroring BackBtn) and navigate back to the
  // list whenever this fires from inside one.
  onClick: async (e, el, s) => {
    const inEditor = !!(s.root.editorDoc || s.root.editorMode)
    if (s.root.editorMode === 'edit') await el.call('flushNoteAutosave')
    const patch = { notesFilter: s.key, selectedNoteId: null, _docsRailOpen: false }
    // "All notes" is the reset row — it clears the folder scope too, so one
    // tap always gets back to the unfiltered list.
    if (s.key === 'all') patch.selectedFolder = 'all'
    if (inEditor) {
      patch.editorDoc = null
      patch.editorMode = null
      patch.activeSection = 'notes'
    }
    s.root.update(patch)
    if (inEditor) _documentsNavigate(el, '/notes')
  },

  // A note card dropped on Shared or Private takes that privacy (2026-10-27
  // per Nika "support to drag and drop notes to sidebar channels"). All notes
  // and AI notes name nothing a person sets, so they take no drop, and nor
  // does the row a note already belongs to. DocsCard marks the drag on the
  // root (`_notesDragId`); the row under it lights through `_notesDropKey`.
  // A drop on Private asks first, the same as the card menu's Make private
  // (functions/noteMenus.js confirmNotePrivacy).
  dataDropTarget: (el, s) => s.root._notesDropKey === 'scope:' + s.key,
  '&[data-drop-target]': {
    background: 'selected',
    color: 'title',
    outlineWidth: 'hairline2',
    outlineStyle: 'dashed',
    outlineColor: 'title',
    outlineOffset: '0'
  },
  onDragover: (e, el, s) => {
    const id = s.root._notesDragId
    if (!id || (s.key !== 'shared' && s.key !== 'private')) return
    const note = (s.root.documents || []).find((d) => d.id === id)
    if (!note || (note.privacy === 'private') === (s.key === 'private')) return
    e.preventDefault()
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'move'
    const key = 'scope:' + s.key
    if (s.root._notesDropKey !== key) s.root.update({ _notesDropKey: key }, { preventFetch: true })
  },
  onDragleave: (e, el, s) => {
    // Moving across the row's own icon and label is not leaving it.
    if (el.node && e.relatedTarget && el.node.contains(e.relatedTarget)) return
    if (s.root._notesDropKey === 'scope:' + s.key)
      s.root.update({ _notesDropKey: null }, { preventFetch: true })
  },
  onDrop: (e, el, s) => {
    const id = s.root._notesDragId
    if (!id || (s.key !== 'shared' && s.key !== 'private')) return
    e.preventDefault()
    s.root.update({ _notesDragId: null, _notesDropKey: null }, { preventFetch: true })
    el.call('confirmNotePrivacy', id, s.key === 'private' ? 'private' : 'team')
  },

  Icon: { name: (el, s) => s.icon || 'edit' },
  Label: { text: (el, s) => el.call('polyglot', s.label || s.key) },

  Count: {
    tag: 'span',
    // The label's size and the house count weight — the Tasks sidebar's
    // counts (13.3px / 500, measured). It was `Y2`, 11px.
    fontSize: 'Z2',
    fontWeight: '500',
    color: 'caption',
    // NOTES-MICRO-1 item 1 — folder + query narrowed (see the header note
    // above `_notesScopedDocs`), same as the grid it sits beside.
    text: (el, s) => {
      const docs = _notesScopedDocs(s)
      return String(docs.filter((n) => _matchesFilter(n, s.key)).length)
    }
  },

  MoreBtn: null
}

// ── Folders ──

// The default taxonomy UNION whatever folders the data actually carries — a
// rename writes new folderPath values, and those must keep a row. (The "all"
// pseudo-folder duplicates the "All notes" filter row above.) A folder of the
// person's own making is `custom` (its row offers Delete, not Unfile —
// functions/noteMenus.js notesFolderMenu). The open folder keeps a row while
// it holds no note yet: a folder is the path its notes carry and the server
// keeps no folder record, so one just made with New folder (DocsNewFolderRow)
// exists only as the open folder until a note goes into it.
const _notesFolderRows = (root) => {
  const base = DOC_FOLDERS.filter((f) => f.key !== 'all')
  const known = new Set(base.map((f) => f.key))
  const extras = []
  const add = (key) => {
    if (!key || key === 'all' || known.has(key)) return
    known.add(key)
    extras.push({ key, label: key, icon: 'folder', custom: true })
  }
  for (const d of root.documents || []) add(d.folder)
  add(root.selectedFolder)
  return [...base, ...extras]
}

// Open a folder's list ('all' = every note) from wherever the sidebar is. The
// /notes/editor route reads editorDoc/editorMode, never selectedFolder, so from
// inside a note it leaves the note first (flushing a pending autosave, as
// BackBtn does) and returns to the list. DocsFolderRow and DocsNewFolderRow
// share it.
const _openNotesFolder = async (el, root, key) => {
  const inEditor = !!(root.editorDoc || root.editorMode)
  if (root.editorMode === 'edit') await el.call('flushNoteAutosave')
  const patch = { selectedFolder: key, selectedDocument: null, _docsRailOpen: false }
  if (inEditor) {
    patch.editorDoc = null
    patch.editorMode = null
    patch.activeSection = 'notes'
  }
  root.update(patch)
  if (inEditor) _documentsNavigate(el, '/notes')
}

export const DocsFolderList = {
  flow: 'y',
  gap: 'V',
  childExtends: 'DocsFolderRow',
  childrenAs: 'state',
  children: (el, s) => _notesFolderRows(s.root)
}

export const DocsFolderRow = {
  extends: 'SidebarRow',
  isActive: (el, s) => s.root.selectedFolder === s.key,
  // CODEX-M2 mobile targets — same 44px floor as DocsNotesFilterRow (see
  // its note; box growth, not tapSlop, because rows stack adjacently).
  '@coarse': { minHeight: 'controlH' },
  color: 'caption',
  '.isActive': { background: 'selected', color: 'title' },
  ':hover': { background: 'hover', color: 'title' },
  'aria-current': (el, s) => (s.root.selectedFolder === s.key ? 'page' : undefined),
  // Same root cause + fix as DocsNotesFilterRow above — this row is also
  // mounted on the /notes/editor route and used to no-op from inside a note.
  onClick: (e, el, s) => {
    if (s.root._renamingFolder === s.key) return
    // Second tap on the active folder clears the scope back to all.
    return _openNotesFolder(el, s.root, s.root.selectedFolder === s.key ? 'all' : s.key)
  },
  // Right-click: Rename folder, then Delete folder (one of the person's own)
  // or Unfile its notes (a built-in one) — functions/noteMenus.js.
  // onContextmenu doesn't bind via DOMQL delegation — native listener.
  onCreate: (el, s) => {
    if (el._cmBound || !el.node) return
    el._cmBound = true
    el.node.addEventListener('contextmenu', (e) => {
      const row = el.state || s
      el.call('notesFolderMenu', e, row.key, { custom: !!row.custom, label: row.label })
    })
  },

  // A note card dropped here moves into this folder (2026-10-27 per Nika
  // "support to drag and drop notes to sidebar channels") — the same write a
  // folder rename makes per note (functions/noteMenus.js moveNoteToFolder).
  // Same contract as DocsNotesFilterRow's drop: the folder a note is already
  // in takes no drop, and the row under the card lights.
  dataDropTarget: (el, s) => s.root._notesDropKey === 'folder:' + s.key,
  '&[data-drop-target]': {
    background: 'selected',
    color: 'title',
    outlineWidth: 'hairline2',
    outlineStyle: 'dashed',
    outlineColor: 'title',
    outlineOffset: '0'
  },
  onDragover: (e, el, s) => {
    const id = s.root._notesDragId
    if (!id) return
    const note = (s.root.documents || []).find((d) => d.id === id)
    if (!note || note.folder === s.key) return
    e.preventDefault()
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'move'
    const key = 'folder:' + s.key
    if (s.root._notesDropKey !== key) s.root.update({ _notesDropKey: key }, { preventFetch: true })
  },
  onDragleave: (e, el, s) => {
    // Moving across the row's own icon and label is not leaving it.
    if (el.node && e.relatedTarget && el.node.contains(e.relatedTarget)) return
    if (s.root._notesDropKey === 'folder:' + s.key)
      s.root.update({ _notesDropKey: null }, { preventFetch: true })
  },
  onDrop: (e, el, s) => {
    const id = s.root._notesDragId
    if (!id) return
    e.preventDefault()
    s.root.update({ _notesDragId: null, _notesDropKey: null }, { preventFetch: true })
    el.call('moveNoteToFolder', id, s.key)
  },

  Icon: { name: (el, s) => s.icon || 'document' },

  Label: {
    if: (el, s) => s.root._renamingFolder !== s.key,
    text: (el, s) => el.call('polyglot', s.label || s.key)
  },

  // Inline rename input — swapped in for the label while this folder is the
  // rename target. Enter commits (bulk folderPath move via
  // renameNotesFolder), Escape/blur cancels.
  RenameInput: {
    tag: 'input',
    type: 'text',
    if: (el, s) => s.root._renamingFolder === s.key,
    flex: '1',
    minWidth: '0',
    padding: '0 Y',
    fontSize: 'Z2',
    color: 'title',
    background: 'transparent',
    borderWidth: '0',
    outline: 'none',
    onCreate: (el, s) => {
      if (!el.node) return
      el.node.value = el.call('polyglot', s.label || s.key)
      el.node.focus()
      el.node.select()
    },
    onClick: (e) => e.stopPropagation(),
    onKeyDown: (e, el, s) => {
      if (e.key === 'Enter') {
        const next = (e.target.value || '').trim()
        if (next && next !== s.key) el.call('renameNotesFolder', s.key, next, s.label)
        else s.root.update({ _renamingFolder: null })
      }
      if (e.key === 'Escape') s.root.update({ _renamingFolder: null })
    },
    onBlur: (e, el, s) => {
      if (s.root._renamingFolder === s.key) s.root.update({ _renamingFolder: null })
    }
  },

  Count: {
    tag: 'span',
    // The label's size and the house count weight (see DocsNotesFilterRow).
    fontSize: 'Z2',
    fontWeight: '500',
    color: 'caption',
    // NOTES-MICRO-1 item 1 — scope + query narrowed (see
    // `_notesScopeFilteredDocs`'s header note above), same as the grid.
    text: (el, s) => {
      const docs = _notesScopeFilteredDocs(s)
      return String(docs.filter((d) => d.folder === s.key).length)
    }
  },

  MoreBtn: null
}

// "New folder" — the folder list's last row (2026-10-30, CRUD standard rule 1:
// a folder a person can file notes into, they can also make; it used to
// appear only by renaming another). The house prompt names it, and the folder
// opens: DocsFolderList shows it while it is still empty, a note started there
// goes into it (functions/startDraftNote.js), and a note moved there keeps it.
// A name that is already a folder — its key or its shown name, any case —
// opens that folder instead of making a twin. Kept here, not in functions/:
// the shell registers this file's components on every route, before /notes is
// visited (packages/workspace/sharedLibraries.js documentsEager), and the
// prompt, polyglot and notesNavigate are registered there too.
export const DocsNewFolderRow = {
  extends: 'SidebarRow',
  icon: 'plus',
  color: 'caption',
  // CODEX-M2 mobile targets — the same 44px floor as DocsFolderRow.
  '@coarse': { minHeight: 'controlH' },
  onClick: (e, el, s) => {
    const root = s.root
    el.call('promptDialog', {
      title: el.call('polyglot', 'documents.folderNew.label'),
      message: el.call('polyglot', 'documents.folderNew.message'),
      placeholder: el.call('polyglot', 'documents.folderNew.placeholder'),
      confirmText: el.call('polyglot', 'common.action.create'),
      cancelText: el.call('polyglot', 'common.action.cancel'),
      onConfirm: (value) => {
        const name = String(value || '')
          .replace(/\s+/g, ' ')
          .trim()
        if (!name) return
        const lower = name.toLowerCase()
        const same = [...DOC_FOLDERS, ..._notesFolderRows(root)].find(
          (f) =>
            f.key.toLowerCase() === lower ||
            String(el.call('polyglot', f.label || f.key)).toLowerCase() === lower
        )
        return _openNotesFolder(el, root, same ? same.key : name)
      }
    })
  },
  Label: { text: (el) => el.call('polyglot', 'documents.folderNew.label') },
  MoreBtn: null
}

// ── Knowledge Base categories ──

export const DocsKbCategoryList = {
  flow: 'y',
  gap: 'V',
  childExtends: 'DocsKbCategoryRow',
  childrenAs: 'state',
  children: (el, s) => KB_CATEGORIES
}

export const DocsKbCategoryRow = {
  extends: 'SidebarRow',
  isActive: (el, s) => s.root.selectedCategory === s.key,
  color: 'caption',
  '.isActive': { background: 'selected', color: 'title' },
  ':hover': { background: 'hover', color: 'title' },
  'aria-current': (el, s) => (s.root.selectedCategory === s.key ? 'page' : undefined),
  onClick: (e, el, s) => {
    s.root.update({ selectedCategory: s.key, _docsRailOpen: false })
  },

  Icon: { name: (el, s) => s.icon || 'book' },
  Label: { text: (el, s) => el.call('polyglot', s.label || s.key) },

  Count: {
    tag: 'span',
    // The label's size and the house count weight (see DocsNotesFilterRow).
    fontSize: 'Z2',
    fontWeight: '500',
    color: 'caption',
    text: (el, s) => {
      const all = s.root.kbArticles || []
      if (s.key === 'all') return String(all.length)
      return String(all.filter((a) => a.category === s.key).length)
    }
  },

  MoreBtn: null
}
